import React, { useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import DraggableField from "../DraggableField/DraggableField";
import Select, { GroupBase } from "react-select";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import {
  handleAddField,
  handleFieldChange,
  handleDeleteField,
  handleSaveFields,
  handleRecipientChange,
  handleCancel,
  openSignatureNav,
  closeSignatureNav,
  onDragEnd
} from "./SignatureFieldsPanel.utils";

import {
    DocumentEditorContainer,
    RecipientOption
} from "../../CreateOrEditEditorComponent";

import styles from "../../../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.module.css";

interface SignatureFieldsPanelProps {
  signaturesOpen: boolean;
  setSignaturesOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectionsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  inputFields: { id: number; value: string }[];
  setInputFields: React.Dispatch<React.SetStateAction<{ id: number; value: string }[]>>;
  recipientOptions: RecipientOption[];
  selectedRecipient: RecipientOption;
  setRecipientOptions: React.Dispatch<React.SetStateAction<RecipientOption[]>>;
  setSelectedRecipient: React.Dispatch<React.SetStateAction<RecipientOption>>;
  signaturesList: RecipientOption[];
  containerRef: React.RefObject<DocumentEditorContainer | null>;
  forwizard?: boolean;
}

// Define the type for the options
type OptionType = { label: string; value: string };

const SignatureFieldsPanel: React.FC<SignatureFieldsPanelProps> = ({
  signaturesOpen,
  setSignaturesOpen,
  setSelectionsOpen,
  inputFields,
  setInputFields,
  recipientOptions,
  selectedRecipient,
  setRecipientOptions,
  setSelectedRecipient,
  signaturesList,
  containerRef,
  forwizard,
}) => {
  const [searchText, setSearchText] = useState("");

  const filteredList = signaturesList.filter((obj) =>
    obj.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const [isAddingFields, setIsAddingFields] = useState(false);

  return (
    <div>
      <div
        className={
          signaturesOpen
            ? "signatures-button-wrapper signatures-active"
            : "signatures-button-wrapper"
        }
        style={{ display: forwizard ? "none" : "" }}
      >
        <Button
          className="flex-column-center-center signatures-button"
          onClick={
            !signaturesOpen
              ? () => openSignatureNav(setSignaturesOpen, setSelectionsOpen, containerRef)
              : () => closeSignatureNav(setSignaturesOpen)
          }
        >
          <Icon.UiChecks
            style={{
              color: "gray",
              fontSize: 16,
              marginBottom: 0,
            }}
          />
          <p>Signatures Fields</p>
        </Button>
      </div>

      <div
        id="signatures"
        className={signaturesOpen ? "signatures fields-section-active" : "signatures"}
      >
        <div className="flex-row-center-start">
          <h6>Recipient(s)</h6>
          <Icon.PlusCircleFill
            className="icon-add-field"
            onClick={() =>
              handleAddField(setIsAddingFields, inputFields, setInputFields)
            }
          />
        </div>

        {isAddingFields ? (
          <div className="recipient-input-fields-wrapper">
            <DragDropContext onDragEnd={(result) => onDragEnd(result, inputFields, setInputFields)}>
              <Droppable droppableId="fields-column">
                {(provided) => (
                  <div
                    className="fields-column"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {inputFields.map((field, index) => (
                      <Draggable key={field.id} draggableId={String(field.id)} index={index}>
                        {(provided) => (
                          <div
                            className="draggable-field"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Icon.GripVertical className="drag-icon" />
                            <input
                              type="text"
                              value={field.value}
                              onChange={(e) =>
                                handleFieldChange(inputFields, setInputFields, field.id, e.target.value)
                              }
                              placeholder="Enter recipient"
                              className="form-control"
                            />
                            <Icon.XCircleFill
                              className="icon-delete-field"
                              onClick={() => handleDeleteField(inputFields, setInputFields, field.id)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            <div className="flex-row-space-center button-group">
              <Button
                onClick={() =>
                  handleSaveFields(inputFields, setRecipientOptions, setSelectedRecipient, selectedRecipient, handleCancel, setIsAddingFields)
                }
                className="custom-button"
              >
                <span>Ok</span>
              </Button>
              <Button onClick={() => handleCancel(setIsAddingFields)} className="custom-button">
                <span>Cancel</span>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <Select<OptionType, false, GroupBase<OptionType>>
                value={selectedRecipient}
                onChange={(e) => handleRecipientChange(setSelectedRecipient, e)}
                options={recipientOptions}
                className="form-select custom-select"
                aria-label="Default"
                closeMenuOnSelect={true}
                placeholder="Select recipient"
                menuPosition="fixed"
                classNamePrefix="react-select"
              />
            </div>
            <div className="signatures-heading-subheading">
              <h6>Signature Fields</h6>
              <p>Drag &amp; drop to place signature.</p>
            </div>
            <div className={styles.dropdownSearchContainer}>
              <Icon.Search className={styles.searchIcon} />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search field..."
                className={styles.dropdownSearch}
              />
            </div>
            <div className="signature-column">
              {filteredList.map((obj, index) => (
                <DraggableField
                  key={index}
                  label={obj.label}
                  value={obj.value}
                  type="Signature"
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SignatureFieldsPanel;
